import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCVContext } from '@/context/CVContext';
import { useLanguage } from '@/context/LanguageContext';
import { profileApi, customSectionsApi } from '@/services/api';
import api from '@/services/api';
import Header from '@/components/Header';
import OpenAppButton from '@/components/OpenAppButton';
import AddSectionModal from '@/components/AddSectionModal';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Download, Edit, MessageCircle, ArrowLeft, Mail, Phone, 
  MapPin, Linkedin, Globe, Calendar, Building2, GraduationCap,
  Briefcase, Code, Languages as LanguagesIcon, Award, Save, X,
  Plus, Trash2, Check, Pencil, Camera, User,
  Brain, FileText, Eye, Sparkles, Users, Heart, HandHeart, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import { downloadCVPdf } from '@/services/pdfService';
import DownloadGateModal from '@/components/DownloadGateModal';
import { useDownloadGate } from '@/hooks/useDownloadGate';
import { trackPDFDownloaded, trackAppExport, trackSectionAdded, trackCVUpdated, trackCVDownloadInitiated, trackCVDownloadCompleted } from '@/lib/posthog';

// Editable text component
const EditableText = ({ value, onChange, placeholder, className = '', multiline = false, disabled = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => {
    setEditValue(value || '');
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleSave = () => {
    setIsEditing(false);
    if (editValue !== value) {
      onChange(editValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !multiline) {
      handleSave();
    }
    if (e.key === 'Escape') {
      setEditValue(value || '');
      setIsEditing(false);
    }
  };

  if (disabled) {
    return <span className={className}>{value || placeholder}</span>;
  }

  if (isEditing) {
    return multiline ? (
      <Textarea
        ref={inputRef}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={`min-h-[80px] ${className}`}
      />
    ) : (
      <Input
        ref={inputRef}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
      />
    );
  }

  return (
    <span 
      className={`cursor-pointer hover:bg-yellow-50 hover:outline hover:outline-2 hover:outline-yellow-300 rounded px-1 transition-all ${className} ${!value ? 'text-gray-400 italic' : ''}`}
      onClick={() => setIsEditing(true)}
      title="Cliquez pour modifier"
    >
      {value || placeholder}
    </span>
  );
};

// Profile Picture Component with upload
const ProfilePicture = ({ profile, onPictureChange }) => {
  const fileInputRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop grande (max 5MB)');
      return;
    }

    try {
      // Compress and convert to base64
      const compressedBase64 = await compressImage(file, 200, 200, 0.8);
      onPictureChange(compressedBase64);
      toast.success('Photo de profil mise à jour !');
    } catch (error) {
      console.error('Error processing image:', error);
      toast.error('Erreur lors du traitement de l\'image');
    }
  };

  const compressImage = (file, maxWidth, maxHeight, quality) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;

          // Calculate new dimensions maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const initials = `${profile.first_name?.[0] || '?'}${profile.last_name?.[0] || ''}`;

  return (
    <div 
      className="relative w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 cursor-pointer group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => fileInputRef.current?.click()}
    >
      {profile.profile_picture ? (
        <img 
          src={profile.profile_picture} 
          alt="Photo de profil"
          className="w-full h-full rounded-full object-cover border-4 border-[#5BA946]"
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-[#5BA946] to-[#4a9339] rounded-full flex items-center justify-center text-white text-2xl sm:text-3xl font-bold">
          {initials}
        </div>
      )}
      
      {/* Overlay on hover */}
      <div className={`absolute inset-0 bg-black/50 rounded-full flex items-center justify-center transition-opacity ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
        <Camera className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
      </div>
      
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
      
      {/* Edit badge */}
      <div className="absolute -bottom-1 -right-1 bg-[#5BA946] rounded-full p-1.5 shadow-lg">
        <Pencil className="w-3 h-3 text-white" />
      </div>
    </div>
  );
};

export default function CVPreview() {
  const navigate = useNavigate();
  const { state, dispatch } = useCVContext();
  const { language, t } = useLanguage();
  const { profile } = state;
  const cvRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [editMode, setEditMode] = useState(true); // Always in edit mode
  const [personalityResult, setPersonalityResult] = useState(null);
  const [loadingPersonality, setLoadingPersonality] = useState(true);
  const [isPrinting, setIsPrinting] = useState(false); // For hiding interactive elements during PDF export
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  const [addSectionType, setAddSectionType] = useState(null);
  
  // Download gate hook - captures lead info before first download
  const {
    showModal: showDownloadGate,
    initiateDownload,
    onLeadCaptured,
    closeModal: closeDownloadGate,
    downloadType,
    isChecking: isCheckingLead
  } = useDownloadGate(profile?.id);

  // Handle add section - opens modal with pre-selected type
  const handleAddSection = (type) => {
    setAddSectionType(type);
    setShowAddSectionModal(true);
  };

  // Handle delete section
  const handleDeleteSection = async (type, id) => {
    if (!profile?.id) return;
    
    try {
      switch (type) {
        case 'reference':
          await customSectionsApi.deleteReference(profile.id, id);
          toast.success(language === 'en' ? 'Reference deleted' : 'Référence supprimée');
          break;
        case 'volunteering':
          await customSectionsApi.deleteVolunteering(profile.id, id);
          toast.success(language === 'en' ? 'Volunteering deleted' : 'Bénévolat supprimé');
          break;
        case 'publication':
          await customSectionsApi.deletePublication(profile.id, id);
          toast.success(language === 'en' ? 'Publication deleted' : 'Publication supprimée');
          break;
        case 'custom':
          await customSectionsApi.deleteCustomSection(profile.id, id);
          toast.success(language === 'en' ? 'Section deleted' : 'Section supprimée');
          break;
        default:
          break;
      }
      // Refresh profile
      refreshProfile();
    } catch (error) {
      console.error('Error deleting section:', error);
      toast.error(language === 'en' ? 'Error deleting' : 'Erreur lors de la suppression');
    }
  };

  // Refresh profile from server
  const refreshProfile = async () => {
    if (!profile?.id) return;
    try {
      const response = await profileApi.get(profile.id);
      if (response.data) {
        dispatch({ type: 'SET_PROFILE', payload: response.data });
      }
    } catch (error) {
      console.error('Error refreshing profile:', error);
    }
  };

  // Check if personality test exists - from localStorage first, then API
  useEffect(() => {
    const checkPersonality = async () => {
      // First check localStorage for personality result
      const savedResult = localStorage.getItem('personalityResult');
      if (savedResult) {
        try {
          const parsed = JSON.parse(savedResult);
          if (parsed.result) {
            setPersonalityResult(parsed.result);
            setLoadingPersonality(false);
            return;
          }
        } catch (e) {
          console.error('Error parsing saved personality result:', e);
        }
      }

      // Fallback to API if profile exists
      if (profile?.id) {
        try {
          const response = await api.get(`/personality/result/${profile.id}`);
          if (response.data.success) {
            setPersonalityResult(response.data.result);
          }
        } catch (error) {
          // No personality result found
          console.log('No personality result for this profile');
        }
      }
      setLoadingPersonality(false);
    };
    checkPersonality();
  }, [profile?.id]);

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Aucun profil trouvé</p>
          <Button onClick={() => navigate('/')}>Commencer</Button>
        </div>
      </div>
    );
  }

  // Update profile field
  const updateProfile = async (field, value) => {
    const updatedProfile = { ...profile };
    
    // Handle nested fields like contact.email
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      updatedProfile[parent] = { ...updatedProfile[parent], [child]: value };
    } else {
      updatedProfile[field] = value;
    }
    
    dispatch({ type: 'SET_PROFILE', payload: updatedProfile });
    setHasChanges(true);
    
    // Auto-save after short delay
    try {
      await profileApi.update(profile.id, { [field.includes('.') ? field.split('.')[0] : field]: field.includes('.') ? updatedProfile[field.split('.')[0]] : value });
    } catch (error) {
      console.error('Error saving:', error);
    }
  };

  // Update array item (education, experience, etc.)
  const updateArrayItem = async (arrayName, index, field, value) => {
    const updatedArray = [...(profile[arrayName] || [])];
    if (updatedArray[index]) {
      updatedArray[index] = { ...updatedArray[index], [field]: value };
      
      const updatedProfile = { ...profile, [arrayName]: updatedArray };
      dispatch({ type: 'SET_PROFILE', payload: updatedProfile });
      setHasChanges(true);
      
      try {
        await profileApi.update(profile.id, { [arrayName]: updatedArray });
      } catch (error) {
        console.error('Error saving:', error);
      }
    }
  };

  // Add new item to array
  const addArrayItem = async (arrayName, newItem) => {
    const updatedArray = [...(profile[arrayName] || []), newItem];
    const updatedProfile = { ...profile, [arrayName]: updatedArray };
    dispatch({ type: 'SET_PROFILE', payload: updatedProfile });
    setHasChanges(true);
    
    try {
      await profileApi.update(profile.id, { [arrayName]: updatedArray });
    } catch (error) {
      console.error('Error saving:', error);
    }
  };

  // Remove item from array
  const removeArrayItem = async (arrayName, index) => {
    const updatedArray = [...(profile[arrayName] || [])];
    updatedArray.splice(index, 1);
    
    const updatedProfile = { ...profile, [arrayName]: updatedArray };
    dispatch({ type: 'SET_PROFILE', payload: updatedProfile });
    setHasChanges(true);
    
    try {
      await profileApi.update(profile.id, { [arrayName]: updatedArray });
    } catch (error) {
      console.error('Error saving:', error);
    }
  };

  // Add skill
  const addSkill = async () => {
    const skill = prompt('Nouvelle compétence:');
    if (skill && skill.trim()) {
      const updatedSkills = [...(profile.skills || []), skill.trim()];
      const updatedProfile = { ...profile, skills: updatedSkills };
      dispatch({ type: 'SET_PROFILE', payload: updatedProfile });
      
      try {
        await profileApi.update(profile.id, { skills: updatedSkills });
        toast.success('Compétence ajoutée');
      } catch (error) {
        console.error('Error saving:', error);
      }
    }
  };

  // Remove skill
  const removeSkill = async (index, isHardSkill = true) => {
    const { hardSkills, softSkills } = categorizeSkills(profile.skills || []);
    let updatedSkills;
    
    if (isHardSkill) {
      hardSkills.splice(index, 1);
      updatedSkills = [...hardSkills, ...softSkills];
    } else {
      softSkills.splice(index, 1);
      updatedSkills = [...hardSkills, ...softSkills];
    }
    
    const updatedProfile = { ...profile, skills: updatedSkills };
    dispatch({ type: 'SET_PROFILE', payload: updatedProfile });
    
    try {
      await profileApi.update(profile.id, { skills: updatedSkills });
    } catch (error) {
      console.error('Error saving:', error);
    }
  };

  // Categorize skills into hard skills and soft skills
  const categorizeSkills = (skills) => {
    const softSkillKeywords = [
      // French soft skills
      'leadership', 'communication', 'travail d\'équipe', 'esprit d\'équipe', 'adaptabilité',
      'créativité', 'résolution de problèmes', 'gestion du temps', 'organisation',
      'autonomie', 'rigueur', 'curiosité', 'empathie', 'négociation', 'présentation',
      'collaboration', 'motivation', 'flexibilité', 'initiative', 'écoute',
      'pédagogie', 'mentoring', 'coaching', 'gestion de projet', 'management',
      'innovation', 'entrepreneuriat', 'conseil', 'stratégique', 'analyse',
      'rédaction', 'blogging', 'networking', 'relationnel',
      // English soft skills  
      'teamwork', 'problem solving', 'time management', 'critical thinking',
      'decision making', 'interpersonal', 'emotional intelligence', 'conflict resolution',
      'public speaking', 'networking', 'mentorship', 'strategic thinking'
    ];

    const hardSkills = [];
    const softSkills = [];

    (skills || []).forEach(skill => {
      const skillLower = skill.toLowerCase();
      const isSoftSkill = softSkillKeywords.some(keyword => 
        skillLower.includes(keyword.toLowerCase())
      );
      
      if (isSoftSkill) {
        softSkills.push(skill);
      } else {
        hardSkills.push(skill);
      }
    });

    return { hardSkills, softSkills };
  };

  // Actual PDF download function - uses server-side generation
  const executeDownloadPDF = async () => {
    if (!profile?.id) {
      toast.error('Erreur: Profil non trouvé');
      return;
    }

    // Show loading toast
    toast.loading('Génération du PDF en cours...', { id: 'pdf-loading' });

    try {
      trackCVDownloadInitiated(profile.id, 'cv_pdf');
      // Use server-side PDF generation for professional quality
      await downloadCVPdf(
        profile.id,
        language === 'en' ? 'en' : 'fr',
        profile?.first_name || 'CV',
        profile?.last_name || ''
      );
      
      // Track PDF download in PostHog
      trackPDFDownloaded(profile.id, language);
      
      trackCVDownloadCompleted(profile.id);
      toast.dismiss('pdf-loading');
      toast.success('PDF téléchargé avec succès !');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.dismiss('pdf-loading');
      toast.error('Erreur lors de la génération du PDF. Veuillez réessayer.');
    }
  };

  // Handler that checks for lead capture first
  const handleDownloadPDF = () => {
    initiateDownload(executeDownloadPDF, 'cv_pdf');
  };

  const fullName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Votre Nom';

  // Handle profile picture change
  const handlePictureChange = async (base64Image) => {
    const updatedProfile = { ...profile, profile_picture: base64Image };
    dispatch({ type: 'SET_PROFILE', payload: updatedProfile });
    
    try {
      await profileApi.update(profile.id, { profile_picture: base64Image });
    } catch (error) {
      console.error('Error saving profile picture:', error);
      toast.error('Erreur lors de la sauvegarde de la photo');
    }
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <Header />
      
      {/* Download Gate Modal */}
      <DownloadGateModal
        isOpen={showDownloadGate}
        onClose={closeDownloadGate}
        onSuccess={onLeadCaptured}
        profileId={profile?.id}
        downloadType={downloadType}
      />
      
      {/* Mobile-optimized main container */}
      <main className="max-w-5xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
        {/* Action Bar - Mobile responsive */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mb-4 sm:mb-6">
          <Button 
            variant="outline" 
            onClick={() => navigate('/chat')}
            className="gap-2 w-full sm:w-auto"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="sm:inline">Retour au chat</span>
          </Button>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            {/* Hide instruction on mobile */}
            <div className="hidden sm:flex text-sm text-gray-500 items-center gap-2 bg-yellow-50 px-3 py-1.5 rounded-lg border border-yellow-200">
              <Pencil className="w-4 h-4 text-yellow-600" />
              <span>{language === 'en' ? 'Click on any text to edit it' : 'Cliquez sur un texte pour le modifier'}</span>
            </div>
            <Button 
              onClick={() => navigate('/complete-profile')}
              variant="outline"
              className="gap-2 border-purple-300 text-purple-600 hover:bg-purple-50 w-full sm:w-auto"
              data-testid="complete-profile-btn"
            >
              <Sparkles className="w-4 h-4" />
              {language === 'en' ? 'Complete Profile' : 'Profil Complet'}
            </Button>
            <Button 
              onClick={handleDownloadPDF}
              className="gap-2 bg-[#5BA946] hover:bg-[#4a9339] w-full sm:w-auto"
              data-testid="download-btn"
            >
              <Download className="w-4 h-4" />
              {language === 'en' ? 'Download PDF' : 'Télécharger PDF'}
            </Button>
            <OpenAppButton 
              profileId={profile?.id}
              className="w-full sm:w-auto"
              variant="header"
            />
          </div>
        </div>

        {/* Mobile instruction banner */}
        <div className="sm:hidden bg-yellow-50 px-3 py-2 rounded-lg border border-yellow-200 mb-4 text-center">
          <p className="text-sm text-gray-600 flex items-center justify-center gap-2">
            <Pencil className="w-4 h-4 text-yellow-600" />
            Touchez un texte pour le modifier
          </p>
        </div>

        {/* CV Preview - Mobile optimized */}
        <Card className="shadow-xl overflow-hidden" data-testid="cv-preview">
          <div ref={cvRef} className="bg-white p-4 sm:p-8 min-h-[800px] sm:min-h-[1000px]">
            {/* Header Section - Mobile responsive */}
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6 mb-6 sm:mb-8">
              {/* Profile Picture with upload */}
              <ProfilePicture 
                profile={profile} 
                onPictureChange={handlePictureChange}
              />
              
              <div className="flex-1 text-center sm:text-left w-full">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">
                  <EditableText 
                    value={profile.first_name} 
                    onChange={(v) => updateProfile('first_name', v)}
                    placeholder="Prénom"
                    className="mr-2"
                  />
                  <EditableText 
                    value={profile.last_name} 
                    onChange={(v) => updateProfile('last_name', v)}
                    placeholder="Nom"
                  />
                </h1>
                <p className="text-lg sm:text-xl text-[#5BA946] font-medium mb-2 sm:mb-3">
                  <EditableText 
                    value={profile.target_job} 
                    onChange={(v) => updateProfile('target_job', v)}
                    placeholder="Poste recherché"
                  />
                </p>
                
                {/* Contact Info - Mobile responsive */}
                <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-4 text-sm text-gray-600">
                  <span className="flex items-center justify-center sm:justify-start gap-1">
                    <Mail className="w-4 h-4" />
                    <EditableText 
                      value={profile.contact?.email || profile.email} 
                      onChange={(v) => updateProfile('contact.email', v)}
                      placeholder="email@example.com"
                    />
                  </span>
                  <span className="flex items-center justify-center sm:justify-start gap-1">
                    <Phone className="w-4 h-4" />
                    <EditableText 
                      value={profile.contact?.phone} 
                      onChange={(v) => updateProfile('contact.phone', v)}
                      placeholder="+33 6 00 00 00 00"
                    />
                  </span>
                  <span className="flex items-center justify-center sm:justify-start gap-1">
                    <MapPin className="w-4 h-4" />
                    <EditableText 
                      value={profile.contact?.address} 
                      onChange={(v) => updateProfile('contact.address', v)}
                      placeholder="Ville, Pays"
                    />
                  </span>
                </div>
              </div>
            </div>

            {/* Bio / Summary */}
            <section className="mb-6 sm:mb-8">
              <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-2 sm:mb-3 flex items-center gap-2 border-b-2 border-[#5BA946] pb-2">
                <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 text-[#5BA946]" />
                Profil Professionnel
              </h2>
              <EditableText 
                value={profile.bio} 
                onChange={(v) => updateProfile('bio', v)}
                placeholder="Décrivez votre profil professionnel en quelques phrases..."
                multiline
                className="text-gray-700 leading-relaxed block w-full text-sm sm:text-base"
              />
            </section>

            {/* Experience */}
            <section className="mb-6 sm:mb-8">
              <div className="flex items-center justify-between border-b-2 border-[#5BA946] pb-2 mb-2 sm:mb-3">
                <h2 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-[#5BA946]" />
                  Expérience Professionnelle
                </h2>
                {!isPrinting && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => addArrayItem('experience', { id: Date.now().toString(), position: '', company: '', description: '' })}
                    className="text-[#5BA946] hover:text-green-700"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Ajouter
                  </Button>
                )}
              </div>
              <div className="space-y-4">
                {(profile.experience || []).map((exp, index) => (
                  <div key={exp.id || index} className="border-l-2 border-gray-200 pl-4 relative group">
                    {!isPrinting && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeArrayItem('experience', index)}
                        className="absolute -left-2 top-0 opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          <EditableText 
                            value={exp.position} 
                            onChange={(v) => updateArrayItem('experience', index, 'position', v)}
                            placeholder="Poste occupé"
                          />
                        </h3>
                        <p className="text-[#5BA946] font-medium">
                          <EditableText 
                            value={exp.company} 
                            onChange={(v) => updateArrayItem('experience', index, 'company', v)}
                            placeholder="Nom de l'entreprise"
                          />
                        </p>
                      </div>
                      <span className="text-sm text-gray-500 flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <EditableText 
                          value={exp.start_date} 
                          onChange={(v) => updateArrayItem('experience', index, 'start_date', v)}
                          placeholder="Début"
                          className="w-20"
                        />
                        {' - '}
                        <EditableText 
                          value={exp.end_date} 
                          onChange={(v) => updateArrayItem('experience', index, 'end_date', v)}
                          placeholder="Fin"
                          className="w-20"
                        />
                      </span>
                    </div>
                    <div className="mt-2">
                      <EditableText 
                        value={exp.description} 
                        onChange={(v) => updateArrayItem('experience', index, 'description', v)}
                        placeholder="Description du poste et réalisations..."
                        multiline
                        className="text-gray-600 text-sm block w-full"
                      />
                    </div>
                  </div>
                ))}
                {(!profile.experience || profile.experience.length === 0) && !isPrinting && (
                  <p className="text-gray-400 italic">Cliquez sur "Ajouter" pour ajouter une expérience</p>
                )}
              </div>
            </section>

            {/* Education */}
            <section className="mb-8">
              <div className="flex items-center justify-between border-b-2 border-[#5BA946] pb-2 mb-3">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-[#5BA946]" />
                  Formation
                </h2>
                {!isPrinting && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => addArrayItem('education', { id: Date.now().toString(), degree: '', institution: '', end_date: '' })}
                    className="text-[#5BA946] hover:text-green-700"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Ajouter
                  </Button>
                )}
              </div>
              <div className="space-y-4">
                {(profile.education || []).map((edu, index) => (
                  <div key={edu.id || index} className="border-l-2 border-gray-200 pl-4 relative group">
                    {!isPrinting && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeArrayItem('education', index)}
                        className="absolute -left-2 top-0 opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          <EditableText 
                            value={edu.degree} 
                            onChange={(v) => updateArrayItem('education', index, 'degree', v)}
                            placeholder="Diplôme"
                          />
                          {' '}
                          <EditableText 
                            value={edu.field_of_study} 
                            onChange={(v) => updateArrayItem('education', index, 'field_of_study', v)}
                            placeholder="en Spécialité"
                            className="text-gray-600"
                          />
                        </h3>
                        <p className="text-[#5BA946] font-medium">
                          <EditableText 
                            value={edu.institution} 
                            onChange={(v) => updateArrayItem('education', index, 'institution', v)}
                            placeholder="Établissement"
                          />
                        </p>
                      </div>
                      <span className="text-sm text-gray-500 flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <EditableText 
                          value={edu.end_date} 
                          onChange={(v) => updateArrayItem('education', index, 'end_date', v)}
                          placeholder="Année"
                          className="w-16"
                        />
                      </span>
                    </div>
                  </div>
                ))}
                {(!profile.education || profile.education.length === 0) && !isPrinting && (
                  <p className="text-gray-400 italic">Cliquez sur "Ajouter" pour ajouter une formation</p>
                )}
              </div>
            </section>

            {/* Skills Section - Hard Skills & Soft Skills */}
            <section className="mb-8">
              <div className="flex items-center justify-between border-b-2 border-[#5BA946] pb-2 mb-4">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Code className="w-5 h-5 text-[#5BA946]" />
                  Compétences
                </h2>
                {!isPrinting && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={addSkill}
                    className="text-[#5BA946] hover:text-green-700"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Ajouter
                  </Button>
                )}
              </div>
              
              {(() => {
                const { hardSkills, softSkills } = categorizeSkills(profile.skills || []);
                const displayHardSkills = hardSkills.slice(0, 10);
                const displaySoftSkills = softSkills.slice(0, 10);
                const hiddenHardCount = hardSkills.length - 10;
                const hiddenSoftCount = softSkills.length - 10;
                
                return (
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Hard Skills */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <Code className="w-4 h-4 text-blue-500" />
                        Hard Skills
                        {hardSkills.length > 0 && (
                          <span className="text-xs text-gray-400">({Math.min(hardSkills.length, 10)}/{hardSkills.length})</span>
                        )}
                      </h3>
                      <div className="flex flex-wrap justify-start gap-2">
                        {displayHardSkills.map((skill, index) => (
                          <Badge 
                            key={index} 
                            className={`bg-blue-100 text-blue-800 ${!isPrinting ? 'hover:bg-red-100 hover:text-red-800 cursor-pointer' : ''} transition-colors group`}
                            onClick={!isPrinting ? () => removeSkill(index, true) : undefined}
                            title={!isPrinting ? "Cliquez pour supprimer" : undefined}
                          >
                            {skill}
                            {!isPrinting && <X className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100" />}
                          </Badge>
                        ))}
                        {hiddenHardCount > 0 && (
                          <Badge className="bg-gray-100 text-gray-500">
                            +{hiddenHardCount} autres
                          </Badge>
                        )}
                        {hardSkills.length === 0 && (
                          <p className="text-gray-400 italic text-sm">Aucune hard skill</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Soft Skills */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <User className="w-4 h-4 text-purple-500" />
                        Soft Skills
                        {softSkills.length > 0 && (
                          <span className="text-xs text-gray-400">({Math.min(softSkills.length, 10)}/{softSkills.length})</span>
                        )}
                      </h3>
                      <div className="flex flex-wrap justify-start gap-2">
                        {displaySoftSkills.map((skill, index) => (
                          <Badge 
                            key={index} 
                            className={`bg-purple-100 text-purple-800 ${!isPrinting ? 'hover:bg-red-100 hover:text-red-800 cursor-pointer' : ''} transition-colors group`}
                            onClick={!isPrinting ? () => removeSkill(index, false) : undefined}
                            title={!isPrinting ? "Cliquez pour supprimer" : undefined}
                          >
                            {skill}
                            {!isPrinting && <X className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100" />}
                          </Badge>
                        ))}
                        {hiddenSoftCount > 0 && (
                          <Badge className="bg-gray-100 text-gray-500">
                            +{hiddenSoftCount} autres
                          </Badge>
                        )}
                        {softSkills.length === 0 && (
                          <p className="text-gray-400 italic text-sm">Aucune soft skill</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </section>

            {/* Languages */}
            <section>
                <div className="flex items-center justify-between border-b-2 border-[#5BA946] pb-2 mb-3">
                  <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <LanguagesIcon className="w-5 h-5 text-[#5BA946]" />
                    Langues
                  </h2>
                  {!isPrinting && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => addArrayItem('languages', { name: '', level: '' })}
                      className="text-[#5BA946] hover:text-green-700"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Ajouter
                    </Button>
                  )}
                </div>
                <div className="space-y-2">
                  {(profile.languages || []).map((lang, index) => (
                    <div key={index} className="flex justify-between items-center group">
                      <EditableText 
                        value={lang.name} 
                        onChange={(v) => updateArrayItem('languages', index, 'name', v)}
                        placeholder="Langue"
                        className="font-medium text-gray-800"
                        disabled={isPrinting}
                      />
                      <div className="flex items-center gap-2">
                        <EditableText 
                          value={lang.level} 
                          onChange={(v) => updateArrayItem('languages', index, 'level', v)}
                          placeholder="Niveau"
                          className="text-sm"
                          disabled={isPrinting}
                        />
                        {!isPrinting && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeArrayItem('languages', index)}
                            className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 p-1 h-auto"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {(!profile.languages || profile.languages.length === 0) && !isPrinting && (
                    <p className="text-gray-400 italic text-sm">Cliquez sur "Ajouter" pour ajouter des langues</p>
                  )}
                </div>
              </section>

            {/* Certifications */}
            <section className="mt-8">
              <div className="flex items-center justify-between border-b-2 border-[#5BA946] pb-2 mb-3">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Award className="w-5 h-5 text-[#5BA946]" />
                  Certifications
                </h2>
                {!isPrinting && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => addArrayItem('certifications', { id: Date.now().toString(), name: '', issuer: '', date: '' })}
                    className="text-[#5BA946] hover:text-green-700"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Ajouter
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                {(profile.certifications || []).map((cert, index) => (
                  <div key={cert.id || index} className="flex justify-between items-center group">
                    <div className="flex items-center gap-2">
                      <EditableText 
                        value={cert.name} 
                        onChange={(v) => updateArrayItem('certifications', index, 'name', v)}
                        placeholder="Nom de la certification"
                        className="font-medium text-gray-800"
                      />
                      <span className="text-gray-400">-</span>
                      <EditableText 
                        value={cert.issuer} 
                        onChange={(v) => updateArrayItem('certifications', index, 'issuer', v)}
                        placeholder="Organisme"
                        className="text-gray-500 text-sm"
                        disabled={isPrinting}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <EditableText 
                        value={cert.date} 
                        onChange={(v) => updateArrayItem('certifications', index, 'date', v)}
                        placeholder="Date"
                        className="text-sm text-gray-500"
                        disabled={isPrinting}
                      />
                      {!isPrinting && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeArrayItem('certifications', index)}
                          className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 p-1 h-auto"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                {(!profile.certifications || profile.certifications.length === 0) && !isPrinting && (
                  <p className="text-gray-400 italic text-sm">Optionnel - Cliquez sur "Ajouter" si vous avez des certifications</p>
                )}
              </div>
            </section>

            {/* References Section - Unified UX */}
            <section className="mt-6 sm:mt-8">
              <div className="flex items-center justify-between border-b-2 border-[#5BA946] pb-2 mb-3">
                <h2 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 text-[#5BA946]" />
                  {language === 'en' ? 'References' : 'Références'}
                </h2>
                {!isPrinting && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleAddSection('references')}
                    className="text-[#5BA946] hover:text-green-700 text-xs sm:text-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" /> {language === 'en' ? 'Add' : 'Ajouter'}
                  </Button>
                )}
              </div>
              {profile.references?.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {profile.references.map((ref, index) => (
                    <div key={ref.id || index} className="p-3 bg-gray-50 rounded-lg relative group">
                      {!isPrinting && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteSection('reference', ref.id)}
                          className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 p-1 h-auto"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                      <p className="font-semibold text-gray-900 text-sm sm:text-base pr-6">{ref.name}</p>
                      {ref.position && <p className="text-xs sm:text-sm text-gray-600">{ref.position}</p>}
                      {ref.company && <p className="text-xs sm:text-sm text-[#5BA946]">{ref.company}</p>}
                      {ref.email && (
                        <p className="text-xs sm:text-sm text-gray-500 flex items-center gap-1 mt-1">
                          <Mail className="w-3 h-3" /> {ref.email}
                        </p>
                      )}
                      {ref.phone && (
                        <p className="text-xs sm:text-sm text-gray-500 flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {ref.phone}
                        </p>
                      )}
                      {ref.relationship && (
                        <p className="text-xs text-gray-400 mt-1 italic">{ref.relationship}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                !isPrinting && <p className="text-gray-400 italic text-sm">{language === 'en' ? 'Click "Add" to add references' : 'Cliquez sur "Ajouter" pour ajouter des références'}</p>
              )}
            </section>

            {/* Interests Section - Unified UX */}
            <section className="mt-6 sm:mt-8">
              <div className="flex items-center justify-between border-b-2 border-[#5BA946] pb-2 mb-3">
                <h2 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Heart className="w-4 h-4 sm:w-5 sm:h-5 text-[#5BA946]" />
                  {language === 'en' ? 'Interests' : 'Centres d\'intérêt'}
                </h2>
                {!isPrinting && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleAddSection('interests')}
                    className="text-[#5BA946] hover:text-green-700 text-xs sm:text-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" /> {language === 'en' ? 'Add' : 'Ajouter'}
                  </Button>
                )}
              </div>
              {profile.interests?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {profile.interests.map((interest, index) => (
                    <Badge 
                      key={index} 
                      className="bg-pink-100 text-pink-800 px-2 sm:px-3 py-1 text-xs sm:text-sm"
                    >
                      {interest}
                    </Badge>
                  ))}
                </div>
              ) : (
                !isPrinting && <p className="text-gray-400 italic text-sm">{language === 'en' ? 'Click "Add" to add interests' : 'Cliquez sur "Ajouter" pour ajouter des centres d\'intérêt'}</p>
              )}
            </section>

            {/* Volunteering Section - Unified UX */}
            <section className="mt-6 sm:mt-8">
              <div className="flex items-center justify-between border-b-2 border-[#5BA946] pb-2 mb-3">
                <h2 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2">
                  <HandHeart className="w-4 h-4 sm:w-5 sm:h-5 text-[#5BA946]" />
                  {language === 'en' ? 'Volunteering' : 'Bénévolat'}
                </h2>
                {!isPrinting && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleAddSection('volunteering')}
                    className="text-[#5BA946] hover:text-green-700 text-xs sm:text-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" /> {language === 'en' ? 'Add' : 'Ajouter'}
                  </Button>
                )}
              </div>
              {profile.volunteering?.length > 0 ? (
                <div className="space-y-3 sm:space-y-4">
                  {profile.volunteering.map((vol, index) => (
                    <div key={vol.id || index} className="border-l-2 border-green-200 pl-3 sm:pl-4 relative group">
                      {!isPrinting && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteSection('volunteering', vol.id)}
                          className="absolute -left-2 top-0 opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 hover:bg-red-50 p-1 h-auto"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1">
                        <div>
                          <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{vol.role || vol.organization}</h3>
                          <p className="text-[#5BA946] font-medium text-xs sm:text-sm">{vol.organization}</p>
                        </div>
                        {(vol.start_date || vol.end_date) && (
                          <span className="text-xs sm:text-sm text-gray-500 flex items-center gap-1">
                            <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                            {vol.start_date} {vol.end_date && `- ${vol.end_date}`}
                          </span>
                        )}
                      </div>
                      {vol.description && (
                        <p className="mt-2 text-gray-600 text-xs sm:text-sm">{vol.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                !isPrinting && <p className="text-gray-400 italic text-sm">{language === 'en' ? 'Click "Add" to add volunteering' : 'Cliquez sur "Ajouter" pour ajouter du bénévolat'}</p>
              )}
            </section>

            {/* Publications Section - Unified UX */}
            <section className="mt-6 sm:mt-8">
              <div className="flex items-center justify-between border-b-2 border-[#5BA946] pb-2 mb-3">
                <h2 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-[#5BA946]" />
                  Publications
                </h2>
                {!isPrinting && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleAddSection('publications')}
                    className="text-[#5BA946] hover:text-green-700 text-xs sm:text-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" /> {language === 'en' ? 'Add' : 'Ajouter'}
                  </Button>
                )}
              </div>
              {profile.publications?.length > 0 ? (
                <div className="space-y-3">
                  {profile.publications.map((pub, index) => (
                    <div key={pub.id || index} className="border-l-2 border-purple-200 pl-3 sm:pl-4 relative group">
                      {!isPrinting && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteSection('publication', pub.id)}
                          className="absolute -left-2 top-0 opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 hover:bg-red-50 p-1 h-auto"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1">
                        <div>
                          <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{pub.title}</h3>
                          {pub.publisher && <p className="text-purple-600 text-xs sm:text-sm">{pub.publisher}</p>}
                        </div>
                        {pub.date && (
                          <span className="text-xs sm:text-sm text-gray-500">{pub.date}</span>
                        )}
                      </div>
                      {pub.description && (
                        <p className="mt-1 text-gray-600 text-xs sm:text-sm">{pub.description}</p>
                      )}
                      {pub.url && (
                        <a 
                          href={pub.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs sm:text-sm text-blue-500 hover:underline mt-1 inline-block"
                        >
                          {language === 'en' ? 'View publication' : 'Voir la publication'}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                !isPrinting && <p className="text-gray-400 italic text-sm">{language === 'en' ? 'Click "Add" to add publications' : 'Cliquez sur "Ajouter" pour ajouter des publications'}</p>
              )}
            </section>

            {/* Custom Sections - Unified UX */}
            {profile.custom_sections?.length > 0 && profile.custom_sections.map((section) => (
              <section key={section.id} className="mt-6 sm:mt-8">
                <div className="flex items-center justify-between border-b-2 border-[#5BA946] pb-2 mb-3">
                  <h2 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2">
                    {section.title}
                  </h2>
                  {!isPrinting && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleDeleteSection('custom', section.id)}
                      className="text-red-500 hover:text-red-700 text-xs sm:text-sm"
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> {language === 'en' ? 'Remove' : 'Supprimer'}
                    </Button>
                  )}
                </div>
                <div className="space-y-3">
                  {section.items?.map((item, index) => (
                    <div key={item.id || index} className="border-l-2 border-orange-200 pl-3 sm:pl-4">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1">
                        <div>
                          <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{item.title}</h3>
                          {item.subtitle && <p className="text-orange-600 text-xs sm:text-sm">{item.subtitle}</p>}
                        </div>
                        {item.date && (
                          <span className="text-xs sm:text-sm text-gray-500">{item.date}</span>
                        )}
                      </div>
                      {item.description && (
                        <p className="mt-1 text-gray-600 text-xs sm:text-sm">{item.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            ))}

            {/* Add Section Button - Opens full section type selector */}
            {!isPrinting && (
              <section className="mt-6 sm:mt-8">
                <Button 
                  variant="outline"
                  onClick={() => handleAddSection(null)}
                  className="w-full border-dashed border-2 border-gray-300 hover:border-[#5BA946] hover:bg-green-50 text-gray-600 hover:text-[#5BA946] gap-2"
                  data-testid="add-custom-section-btn"
                >
                  <Plus className="w-4 h-4" />
                  {language === 'en' ? 'Add a section' : 'Ajouter une section'}
                </Button>
              </section>
            )}

            {/* Bottom Download Button - Duplicated for easier access */}
            {!isPrinting && (
              <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-center gap-3">
                <p className="text-xs sm:text-sm text-gray-500 text-center">
                  {language === 'en' ? 'Your CV is ready!' : 'Votre CV est prêt !'}
                </p>
                <Button 
                  onClick={handleDownloadPDF}
                  className="gap-2 bg-[#5BA946] hover:bg-[#4a9339] w-full sm:w-auto text-sm"
                  data-testid="download-btn-bottom"
                >
                  <Download className="w-4 h-4" />
                  {language === 'en' ? 'Download PDF' : 'Télécharger PDF'}
                </Button>
                <OpenAppButton 
                  profileId={profile?.id}
                  className="w-full sm:w-auto"
                  variant="header"
                />
              </div>
            )}
          </div>
        </Card>

        {/* CTA - Personality Test or Complete Profile */}
        {!loadingPersonality && (
          personalityResult ? (
            /* User has completed personality test - Show Complete Profile CTA */
            <div className="mt-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6">
              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                <div className="w-14 h-14 bg-gradient-to-br from-[#5BA946] to-emerald-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <FileText className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <div className="flex items-center gap-2 justify-center sm:justify-start mb-1">
                    <h3 className="text-lg font-bold text-gray-900">
                      {language === 'en' ? 'Your complete profile is ready!' : 'Votre profil complet est prêt !'}
                    </h3>
                    <Badge className="bg-green-100 text-green-800">
                      {personalityResult.persona.icon} {language === 'en' ? personalityResult.persona.name_en : personalityResult.persona.name_fr}
                    </Badge>
                  </div>
                  <p className="text-gray-600 text-sm">
                    {language === 'en' 
                      ? 'View your CV combined with your personality profile in one professional document.'
                      : 'Consultez votre CV combiné avec votre profil de personnalité dans un document professionnel.'}
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/complete-profile')}
                  className="bg-[#5BA946] hover:bg-[#4a9339] text-white"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {language === 'en' ? 'View complete profile' : 'Voir le profil complet'}
                </Button>
              </div>
            </div>
          ) : (
            /* User hasn't completed personality test - Show Test CTA */
            <div className="mt-6 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-2xl p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <Brain className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">
                    {language === 'en' ? 'Discover your Soft Power' : 'Découvrez votre Soft Power'}
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    {language === 'en' 
                      ? 'Complete your professional profile by revealing your character strengths and leadership style. Stand out to recruiters!'
                      : 'Complétez votre profil professionnel en révélant vos forces de caractère et votre style de leadership. Démarquez-vous auprès des recruteurs !'}
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                    <span className="bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded-full">🧠 Big Five</span>
                    <span className="bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded-full">🌍 {language === 'en' ? 'West Africa' : 'Afrique de l\'Ouest'}</span>
                    <span className="bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded-full">⏱️ 5 min</span>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/personality-test')}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg"
                >
                  {language === 'en' ? 'Take the test' : 'Passer le test'}
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Button>
              </div>
            </div>
          )
        )}

        {/* WhatsApp Continue */}
        <div className="mt-6 sm:mt-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 sm:px-6 py-2 sm:py-3 shadow-sm">
            <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-[#5BA946]" />
            <span className="text-gray-600 text-sm sm:text-base">Besoin d'aide ?</span>
            <Button 
              variant="link" 
              className="p-0 h-auto text-[#5BA946] font-semibold text-sm sm:text-base"
              onClick={() => navigate('/chat')}
              data-testid="whatsapp-help-btn"
            >
              Retour au chat
            </Button>
          </div>
        </div>
      </main>

      {/* Add Section Modal */}
      <AddSectionModal
        isOpen={showAddSectionModal}
        onClose={() => {
          setShowAddSectionModal(false);
          setAddSectionType(null);
        }}
        profileId={profile?.id}
        onSectionAdded={refreshProfile}
        initialType={addSectionType}
      />
    </div>
  );
}
